import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import CustomSubmitButton from '../../../components/Forms/CustomSubmitButton';
import { ManageAccounts } from '@mui/icons-material';
import { colors } from '../../../assets/utils/constants';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { useDispatch, useSelector } from 'react-redux';
import useRTK from '../../../hooks/useRTK';
import {
    useRequestORGPhoneVerificationMutation,
    useVerifyORGPhoneNumberMutation,
} from '../../../api/profile/profileAPI';
import { setUserProfile } from '../../../states/features/profile/profileSlice';
import {
    getRTKErrorMessage,
    getRTKResponseData,
    getRTKSuccessMessage,
} from '../../../helpers/RTKHelpers';
import { authSelector } from '../../../states/features/selectors';
import { setPinId } from '../../../states/features/auth/authSlice';
import { errorNotify, successNotify } from '../../../helpers/notify';
import { useEffect } from 'react';

// * -> verify schema
const otpValidationSchema = Yup.object({
    pin: Yup.number().required('Fill all field').min(6),
});

const PhoneVerification = ({ user, closeDialogy, refetchProfile }) => {
    // ############## Comp State ################
    const [OTP, setOTP] = useState('');

    // ################# CONST ##################
    const RTK = useRTK();
    const dispatch = useDispatch();

    // ############## Redux state ################
    const { pinId } = useSelector(authSelector);

    // ############### RTK #####################
    // => Request
    const [
        requestORGPhoneVerification,
        {
            isLoading: requestLoading,
            isSuccess: phoneSuccess,
            isError: phoneIsError,
            error: phoneError,
            data: phoneData,
        },
    ] = useRequestORGPhoneVerificationMutation();
    // => Verify
    const [
        verifyORGPhone,
        { isLoading, isSuccess, isError, error, data: updatedData },
    ] = useVerifyORGPhoneNumberMutation();

    // ############### useEffect ################
    useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            if (phoneSuccess) {
                dispatch(setPinId(getRTKResponseData(phoneData)));
                successNotify(getRTKSuccessMessage(phoneData));
            }
            if (phoneIsError) {
                errorNotify(getRTKErrorMessage(phoneError));
            }
        }
        return () => {
            isSubscribed = false;
        };
    }, [phoneSuccess, phoneIsError, phoneError, phoneData, dispatch]);
    // => RTK action
    const rtkActions = (data) => {
        closeDialogy();
        refetchProfile();
        dispatch(setUserProfile(data));
    };
    RTK.useRTKResponse(isSuccess, isError, error, updatedData, rtkActions);

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={{
                    pin: OTP,
                }}
                validationSchema={otpValidationSchema}
                onSubmit={(payload) => {
                    payload = {
                        pinId: pinId,
                        ...payload,
                    };
                    verifyORGPhone(payload);
                }}
            >
                {(formik) => (
                    <Form>
                        {requestLoading && (
                            <LinearProgress
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                }}
                            />
                        )}
                        <Grid container justifyContent={'center'}>
                            <Grid item sm={10}>
                                <Typography
                                    sx={{
                                        fontSize: 22,
                                        textAlign: 'center',
                                        color: colors.primary,
                                        fontWeight: 'bold',
                                        margin: 2,
                                    }}
                                >
                                    Verify {user.organization_type.name} phone
                                    number
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        textAlign: 'center',
                                        color: colors.primary,
                                    }}
                                >
                                    An verification code has been sent to{' '}
                                    {user.organization_type.name} phone :{' '}
                                    <strong>{user.phone}</strong>
                                </Typography>
                            </Grid>
                            <Grid item sm={9}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginTop: 2,
                                        marginBottom: 3,
                                        flexDirection: 'column',
                                    }}
                                >
                                    <OTPInput
                                        value={OTP}
                                        onChange={setOTP}
                                        autoFocus
                                        OTPLength={6}
                                        otpType="number"
                                        disabled={false}
                                    />
                                    {isError ? (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: 'red',
                                                    textAlign: 'center',
                                                    marginTop: 4,
                                                }}
                                            >
                                                {getRTKErrorMessage(error)}
                                            </Typography>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                                <ResendOTP
                                    onResendClick={() =>
                                        requestORGPhoneVerification()
                                    }
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <CustomSubmitButton
                                        startIcon={<ManageAccounts />}
                                        onClick={formik.handleSubmit}
                                        loading={isLoading}
                                    >
                                        Verify phone
                                    </CustomSubmitButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default PhoneVerification;
